import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../UserContext';
import { Box, Button, Typography } from '@mui/material';
import ConnectedIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';
import NotConnectedIcon from '@mui/icons-material/LinkOff';
import AppConfig from '../AppConfig';
import { hasFeature } from '../business/Subscription';
import { isXpmConnected, XpmConnectedResponse } from '../rest/BackendApi';
import MessageContext, { error } from '../MessageContext';
import { SimpleLoading } from '../components/Loading';
import { PageContainer, PageHeader, Section } from '../components/Page';
import { Link as RouterLink } from 'react-router';

export type XpmConnectionState = 'connected' | 'reauth' | 'disconnected';

export function getXpmConnectionState(status: XpmConnectedResponse): XpmConnectionState {
  if (!status.connected) {
    return 'disconnected';
  }
  return status.authenticationSuccessful && status.allScopes ? 'connected' : 'reauth';
}

const STATE_DISPLAY = {
  connected: {
    icon: <ConnectedIcon color="success" />,
    title: 'Connected',
    text: 'BAS Ready is matching your Xero organisations to XPM clients by ABN.',
    button: 'Refresh connection',
    variant: 'outlined' as const,
  },
  reauth: {
    icon: <WarningIcon color="warning" />,
    title: 'Needs re-authentication',
    text: 'The connection to XPM has expired or is missing permissions, so XPM jobs are not updating.',
    button: 'Re-authenticate XPM',
    variant: 'contained' as const,
  },
  disconnected: {
    icon: <NotConnectedIcon color="disabled" />,
    title: 'Not connected',
    text: 'Connect XPM to see each client’s XPM job, staff and manager on the dashboard.',
    button: 'Connect XPM',
    variant: 'contained' as const,
  },
};

export default function XpmPage() {
  const userContext = useContext(UserContext);
  const messageContext = useContext(MessageContext);
  const user = userContext?.user;
  const available = !!hasFeature(user, 'XPM');
  const [xpmStatus, setXpmStatus] = useState<XpmConnectedResponse>({ connected: false, authenticationSuccessful: false, allScopes: false });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!available) {
      return;
    }
    setLoading(true);
    isXpmConnected(true)
      .then(xpmConnected => setXpmStatus(xpmConnected))
      .catch(() => messageContext.setMessage(error('Failed to check the XPM connection')))
      .finally(() => setLoading(false));
  }, [user]);

  const header = <PageHeader title="XPM" description="Link Xero Practice Manager to see XPM jobs alongside each client on the BAS dashboard." />;

  if (!user) {
    return null;
  }

  if (!available) {
    return (
      <PageContainer>
        {header}
        <Section title="Not included in your plan" description="XPM integration is available on higher plans.">
          <Button variant="contained" component={RouterLink} to="/subscription?showPricing">
            See plans
          </Button>
        </Section>
      </PageContainer>
    );
  }

  const state = getXpmConnectionState(xpmStatus);
  const display = STATE_DISPLAY[state];

  return (
    <PageContainer>
      {header}
      <Section title="Connection">
        {loading ? (
          <SimpleLoading message="Checking XPM connection" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              {display.icon}
              <Box>
                <Typography sx={{ fontWeight: 600 }} data-testid="xpm-status">
                  {display.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {display.text}
                </Typography>
              </Box>
            </Box>
            <Button variant={display.variant} href={AppConfig.oauthXpmPath}>
              {display.button}
            </Button>
          </Box>
        )}
      </Section>

      <Section title="How it works">
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 }, color: 'text.secondary', fontSize: '0.875rem' }}>
          <li>Connect XPM with a Xero login that has access to your practice.</li>
          <li>BAS Ready matches each Xero organisation to an XPM client using its ABN.</li>
          <li>
            On the dashboard, use the column picker to show the <b>Xpm Job</b>, <b>Xpm Staff</b> and <b>Xpm Manager</b> columns.
          </li>
        </Box>
      </Section>
    </PageContainer>
  );
}
